import React, { useState, useEffect, useRef } from "react";
import {
  PricingModelDTO,
  addPricingModels,
  updatePricingModels,
  deletePricingModel,
  fetchPackageById,
  ChaletType,
} from "../CategoryService";
import { useToast } from "../../../Contexts/ToastContext";

// Date ranges are edited once per package and applied to every pricing option. `key` is the
// start|end the range had when loaded, used to find each pricing option's existing row (and id).
interface EditableDateRange {
  key: string | null;
  startDate: string;
  endDate: string;
}

const toDay = (value?: string | null) => (value ? value.split("T")[0] : "");
const rangeKey = (start?: string | null, end?: string | null) => `${toDay(start)}|${toDay(end)}`;

const addDays = (day: string, days: number): string => {
  const [y, m, d] = day.split("-").map(Number);
  if (!y || !m || !d) return "";
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().split("T")[0];
};


interface PricingModelFormProps {
  packageId: number;
  onPricingModelsAdded: (updatedPricingModels: PricingModelDTO[]) => void;
  isEditing: boolean;
  initialData?: PricingModelDTO[];
  onDirtyChange?: (dirty: boolean) => void;
}

const PricingModelForm: React.FC<PricingModelFormProps> = ({
  packageId,
  onPricingModelsAdded,
  isEditing,
  initialData,
  onDirtyChange,
}) => {
  const { showToast } = useToast();
  const [pricingOptions, setPricingOptions] = useState<PricingModelDTO[]>([]);
  const [dateRanges, setDateRanges] = useState<EditableDateRange[]>([]);
  const [showPricingOptions, setShowPricingOptions] = useState<boolean>(false);
  const [dirty, setDirty] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  // Last server state, used to keep each pricing option's existing date range ids on save
  const serverModels = useRef<PricingModelDTO[]>([]);

  const loadFromModels = (models: PricingModelDTO[]) => {
    serverModels.current = models;

    // Deduplicate pricing options
    const uniquePricingOptions = Array.from(
      new Set(
        models.map((option) =>
          JSON.stringify({
            id: option.id,
            packageId: option.packageId,
            description: option.description,
            price: option.price,
            discount: option.discount,
            minPersons: option.minPersons,
            maxPersons: option.maxPersons,
            bookedPersons: option.bookedPersons,
            chaletCapacity: option.chaletCapacity,
            chaletType:
              option.chaletType === null || option.chaletType === undefined
                ? ChaletType.Big
                : option.chaletType,
          })
        )
      )
    ).map((option) => ({ ...JSON.parse(option), dateRanges: [] } as PricingModelDTO));
    setPricingOptions(uniquePricingOptions);

    // One editable row per distinct start/end across all pricing options (copies, never the props)
    const seen = new Set<string>();
    const unique: EditableDateRange[] = [];
    models.forEach((option) =>
      (option.dateRanges ?? []).forEach((dr) => {
        const key = rangeKey(dr.startDate, dr.endDate);
        if (!seen.has(key)) {
          seen.add(key);
          unique.push({ key, startDate: toDay(dr.startDate), endDate: toDay(dr.endDate) });
        }
      })
    );
    unique.sort((a, b) => a.startDate.localeCompare(b.startDate));
    setDateRanges(unique);
    setDirty(false);
  };

  useEffect(() => {
    if (initialData) {
      loadFromModels(initialData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  useEffect(() => {
    onDirtyChange?.(dirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);

  useEffect(() => {
    return () => onDirtyChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const handleAddDateRange = () => {
    setDateRanges([...dateRanges, { key: null, startDate: "", endDate: "" }]);
    setDirty(true);
  };

  const handleDateRangeChange = (
    index: number,
    field: "startDate" | "endDate",
    value: string
  ) => {
    const newDateRanges = dateRanges.map((dr) => ({ ...dr }));

    if (field === "startDate") {
      newDateRanges[index].startDate = value;
      // Packages run weekly: default the end to start + 7 days (only when the typed date is complete)
      const end = addDays(value, 7);
      if (end) newDateRanges[index].endDate = end;
    } else {
      newDateRanges[index].endDate = value;
    }

    setDateRanges(newDateRanges);
    setDirty(true);
  };

  const handleRemoveDateRange = (index: number) => {
    setDateRanges(dateRanges.filter((_, i) => i !== index));
    setDirty(true);
  };

  const validate = (): string | null => {
    if (pricingOptions.length === 0) {
      return "Add at least one pricing option first - dates are saved together with the pricing options.";
    }
    const seen = new Set<string>();
    for (const range of dateRanges) {
      if (!range.startDate || !range.endDate) {
        return "Every date range needs both a start date and an end date.";
      }
      if (range.endDate <= range.startDate) {
        return `End date must be after the start date (${range.startDate}).`;
      }
      const key = rangeKey(range.startDate, range.endDate);
      if (seen.has(key)) {
        return `The date range ${range.startDate} - ${range.endDate} is listed twice.`;
      }
      seen.add(key);
    }
    return null;
  };

  const handleAddPricingOption = () => {
    setDirty(true);
    setShowPricingOptions(true);
    setPricingOptions([
      ...pricingOptions,
      {
        id: 0,
        packageId,
        description: "",
        price: 0,
        discount: 0,
        minPersons: 1,
        maxPersons: 4,
        dateRanges: [],
        chaletType: ChaletType.Small, // Set default value here
      },
    ]);
  };

  const handlePricingOptionChange = <K extends keyof PricingModelDTO>(
    index: number,
    field: K,
    value: PricingModelDTO[K]
  ) => {
    const newPricingOptions = [...pricingOptions];
    newPricingOptions[index][field] = value;
    setPricingOptions(newPricingOptions);
    setDirty(true);
  };

  const handleChaletTypeChange = (index: number, value: string) => {
    const newPricingOptions = [...pricingOptions];
    newPricingOptions[index].chaletType = parseInt(value) as ChaletType;
    setPricingOptions(newPricingOptions);
    setDirty(true);
  };

  const handleRemovePricingOption = async (index: number) => {
    const pricingModelId = pricingOptions[index].id;

    // Options that were never saved only exist locally
    if (!pricingModelId) {
      setPricingOptions(pricingOptions.filter((_, i) => i !== index));
      setDirty(true);
      return;
    }

    if (!window.confirm("Delete this pricing option? This cannot be undone.")) return;

    // Call the service method to delete the pricing model from the backend
    const response = await deletePricingModel(pricingModelId, packageId, process.env.REACT_APP_API_URL);

    if (response.success) {
      const newPricingOptions = pricingOptions.filter((_, i) => i !== index);
      setPricingOptions(newPricingOptions);
      serverModels.current = serverModels.current.filter((m) => m.id !== pricingModelId);
      showToast("Pricing option deleted.", "success");
    } else {
      showToast(`Failed to delete pricing option: ${response.message}`, "error", 8000);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (saving) return;

    const problem = validate();
    if (problem) {
      showToast(problem, "error", 8000);
      return;
    }

    // The same date ranges go to every pricing option; each option reuses its own existing range id
    // (matched by the range's original dates) so nothing is deleted and re-created needlessly.
    const pricingModels: PricingModelDTO[] = pricingOptions.map((option) => {
      const existing = serverModels.current.find((m) => m.id === option.id);
      return {
        ...option,
        packageId: packageId,
        dateRanges: dateRanges.map((range) => {
          const match = range.key
            ? (existing?.dateRanges ?? []).find((dr) => rangeKey(dr.startDate, dr.endDate) === range.key)
            : undefined;
          return {
            id: match?.id ?? 0,
            startDate: range.startDate,
            endDate: range.endDate,
            pricingModelId: option.id || undefined,
          };
        }),
        chaletType:
          (option as any).chaletType === null || (option as any).chaletType === undefined
            ? ChaletType.Big
            : (option as any).chaletType,
      };
    });

    setSaving(true);
    try {
      const response = isEditing
        ? await updatePricingModels(pricingModels, process.env.REACT_APP_API_URL)
        : await addPricingModels(packageId, pricingModels, process.env.REACT_APP_API_URL);

      if (!response.success) {
        showToast(`Could not save: ${response.message}. Your changes are still on screen - nothing was lost.`, "error", 10000);
        return;
      }

      // The save endpoints only confirm success; reload the real saved state so the screen shows what is stored
      const fresh = await fetchPackageById(packageId, process.env.REACT_APP_API_URL);
      if (fresh.success && fresh.data && Array.isArray(fresh.data.prices)) {
        loadFromModels(fresh.data.prices);
        onPricingModelsAdded(fresh.data.prices);
        showToast("Dates and pricing saved.", "success");
      } else {
        setDirty(false);
        showToast("Saved, but the page could not reload the result. Please refresh to confirm.", "info", 8000);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="container pricing-options-section mb-5 pb-5">
      <form onSubmit={handleSubmit} className="pricing-model-form">
        <div>
          <h4>Date Ranges:</h4>
          <p className="text-muted" style={{ fontSize: "13px" }}>
            These dates apply to <strong>all</strong> pricing options of this package. Nothing is stored
            until you press <strong>Save dates &amp; pricing</strong>.
          </p>
          {dirty && (
            <div className="alert alert-warning py-2">
              You have unsaved changes. Press <strong>Save dates &amp; pricing</strong> to keep them.
            </div>
          )}
          {dateRanges.length === 0 && (
            <p className="text-muted">No dates yet - press "Add Date Range".</p>
          )}
          {dateRanges.map((dateRange, index) => (
            <div key={index} className="date-range">
              <label>Start Date:</label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) =>
                  handleDateRangeChange(index, "startDate", e.target.value)
                }
                disabled={!isEditing}
              />
              <label>End Date:</label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) =>
                  handleDateRangeChange(index, "endDate", e.target.value)
                }
                disabled={!isEditing}
              />
              {isEditing && (
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={() => handleRemoveDateRange(index)}
                >
                  Remove Date Range
                </button>
              )}
            </div>
          ))}
          {isEditing && (
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={handleAddDateRange}
            >
              Add Date Range
            </button>
          )}
        </div>
        {isEditing && (
          <div>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setShowPricingOptions(!showPricingOptions)}
            >
              {showPricingOptions
                ? "Hide Pricing Options"
                : "Show Pricing Options"}
            </button>
          </div>
        )}
        {showPricingOptions && Array.isArray(pricingOptions) && ( // Ensure pricingOptions is an array
          <div>
            <h4>Pricing Options:</h4>
            {pricingOptions.map((option, index) => (
              <div key={index} className="pricing-option">
                <label>Description:</label>
                <input
                  type="text"
                  value={option.description}
                  onChange={(e) =>
                    handlePricingOptionChange(
                      index,
                      "description",
                      e.target.value
                    )
                  }
                  disabled={!isEditing}
                />
                <label>Price:</label>
                <input
                  type="number"
                  value={option.price}
                  onChange={(e) =>
                    handlePricingOptionChange(index, "price", +e.target.value)
                  }
                  disabled={!isEditing}
                />
                <label>Discount:</label>
                <input
                  type="number"
                  value={option.discount}
                  onChange={(e) =>
                    handlePricingOptionChange(
                      index,
                      "discount",
                      +e.target.value
                    )
                  }
                  disabled={!isEditing}
                />
                <label>Min Persons:</label>
                <input
                  type="number"
                  value={option.minPersons}
                  onChange={(e) =>
                    handlePricingOptionChange(
                      index,
                      "minPersons",
                      +e.target.value
                    )
                  }
                  disabled={!isEditing}
                />
                <label>Max Persons:</label>
                <input
                  type="number"
                  value={option.maxPersons}
                  onChange={(e) =>
                    handlePricingOptionChange(
                      index,
                      "maxPersons",
                      +e.target.value
                    )
                  }
                  disabled={!isEditing}
                />
                <label>Chalet Type:</label>
                <select
                  // Bind as string to match option values and normalize null/undefined to Big ("0")
                  value={String(
                    (option as any).chaletType === null || (option as any).chaletType === undefined
                      ? ChaletType.Big
                      : (option as any).chaletType
                  )}
                  onChange={(e) =>
                    handleChaletTypeChange(index, e.target.value)
                  }
                  disabled={!isEditing}
                >
                  <option value={ChaletType.Big.toString()}>Big</option>
                  <option value={ChaletType.Small.toString()}>Small</option>
                </select>
                {isEditing && (
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => handleRemovePricingOption(index)}
                  >
                    Remove Pricing Option
                  </button>
                )}
              </div>
            ))}
            {isEditing && (
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={handleAddPricingOption}
              >
                Add Pricing Option
              </button>
            )}
          </div>
        )}
        {isEditing && (
          <div className="d-grid mt-2 pt-2">
            <button type="submit" className="btn btn-success" disabled={saving}>
              {saving ? "Saving..." : "Save dates & pricing"}
            </button>
          </div>
        )}
      </form>
    </section>
  );
};

export default PricingModelForm;
